import moment from "moment";
import qs from "qs";
import { useCallback, useEffect, useMemo, useState } from "react";
import { useLocation } from "react-router-dom";
import { getCommonFacilityListAsync } from "src/api/building/building-api";
import { CommonFacilityModel } from "src/api/building/building-types";
import { useApiOperation } from "src/api/hooks";
import { getPriceRulesListAsync } from "src/api/price/price-api";
import { PriceRulesModel } from "src/api/price/price-types";
import { getProviderList } from "src/api/provider/provider-api";
import { ProviderModel } from "src/api/provider/provider-types";
import { Order, PageMeta, Sort } from "src/api/public-types";
import { BaseButton, BaseInput, BaseInputWithSearch, BaseSelect, BaseTable } from "src/components";
import RangeDatepicker from "src/components/RangeDatepicker";
import SearchResetButton from "src/components/SearchResetButton";
import { usePartnerAuthority } from "src/hooks/usePartnerAuthority";
import useNavigate from "src/hooks/usePartnerNavigate";
import SearchBuildingPopup from "src/pages/commonPopup/SearchBuildingPopup";
import { PagePath } from "src/pages/product/details";
import { qsParse, YmdFormat } from "src/utils";
import AppliedBasePriceModal from "../components/AppliedBasePriceModal";
import ProviderSelectModal from "../components/ProviderSelectModal";
import priceListColumn from "./columns/priceListColumn";

// url query parameter 타입
type QueryParams = {
  page?: number;
  size?: number;
  sort?: Sort;
  searchType?: string;
  keywordList?: string;
  providerName?: string;
  providerId?: string;
  buildingName?: string;
  buildingId?: string;
  buildingCommonFacilityId?: string;
  isDisplayed?: string;
  containsTypes?: string;
  partnerId?: string;
  partnerName?: string;
  priceRuleType?: string;
  priceRuleIdList?: string;
  startDate?: string;
  endDate?: string;
};

const searchTypes = [
  {
    label: "전체",
    value: "",
  },
  {
    label: "공용공간 요금 ID",
    value: "PRICE_RULE_ID",
  },
  {
    label: "정책명",
    value: "RULE_NAME",
  },
];

const ruleTypes = [
  {
    label: "전체",
    value: "",
  },
  {
    label: "기본 요금",
    value: "PRICE_RULE_PROVIDER,PRICE_RULE_FACILITY",
  },
  {
    label: "개별 요금",
    value: "PRICE_RULE_SPECIFIC",
  },
];

/**
 * 공용공간 요금 > 목록 화면
 */
const PriceList = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const [dateRange, setDateRange] = useState<[Date | null, Date | null]>([null, null]);
  const queryParams: QueryParams = useMemo(() => {
    const _queryParams: QueryParams = qsParse(location.search);

    // page, size, sort 없이 최초 진입했을때 default 값 바인딩
    if (!_queryParams?.page) {
      _queryParams.page = 0;
    }
    if (!_queryParams?.size) {
      _queryParams.size = 20;
    }
    if (!_queryParams?.providerName) {
      _queryParams.providerName = "";
    }
    if (_queryParams?.searchType === undefined) {
      _queryParams.searchType = ""; // 기본값: 전체
    }
    if (!_queryParams?.buildingName) {
      _queryParams.buildingName = "";
    }
    if (!_queryParams?.sort) {
      _queryParams.sort = {
        orders: [{ property: "id", direction: "DESC" }],
      };
    }
    // qs.parse에 decoder때문에 검색값 입력 검색시 한번더 decoding
    if (_queryParams.keywordList) {
      _queryParams.keywordList = decodeURIComponent(_queryParams.keywordList);
    }

    // 적용기간(startDate ~ endDate) 쿼리파람을 datepicker state와 동기화
    const dump: [Date | null, Date | null] = [null, null];
    dump[0] = _queryParams?.startDate ? moment(_queryParams.startDate).toDate() : null;
    dump[1] = _queryParams?.endDate ? moment(_queryParams.endDate).toDate() : null;
    setDateRange(dump);

    return _queryParams;
  }, [location]);

  const [params, setParams] = useState<QueryParams>({ ...queryParams });
  const [pageMeta, setPageMeta] = useState<PageMeta>();
  const [priceList, setPriceList] = useState<PriceRulesModel[]>();
  const [isProviderModalOpen, setIsProviderModalOpen] = useState({ isOpen: false });
  const [isBuildingModalOpen, setIsBuildingModalOpen] = useState({ isOpen: false });
  const [isAppliedBasePriceModalOpen, setIsAppliedBasePriceModalOpen] = useState({ isOpen: false });
  const { executeAsync: getPriceList } = useApiOperation(getPriceRulesListAsync);
  const { executeAsync: getProvider } = useApiOperation(getProviderList);
  const { executeAsync: getCommonFacilityList } = useApiOperation(getCommonFacilityListAsync);
  const { isAuthority } = usePartnerAuthority();
  // 쿼리파람과 함께 navigate
  const navigateWithQueryParams = useCallback(
    (passParams?: QueryParams, type?: "search" | "pagination") => {
      let data;
      if (type) {
        type === "search" ? (data = { ...params }) : (data = { ...queryParams });
      }

      const newQueryParams = { ...data, ...(passParams || {}) };
      const newQueryParamStr = qs.stringify(newQueryParams, { allowDots: true });
      navigate(location.pathname + "?" + decodeURIComponent(newQueryParamStr));
    },
    [navigate, location.pathname, params, queryParams],
  );

  const onAddedProviderSelectModal = (
    provider: {
      providerId: string;
      providerName: string;
    }[],
  ) => {
    if (!provider) return;
    setIsProviderModalOpen({ isOpen: false });
    navigateWithQueryParams({ ...params, providerName: provider[0].providerName, providerId: String(provider[0].providerId) });
  };

  const onAddedBuildingSelectModal = (building: any) => {
    if (!building) return;
    setIsBuildingModalOpen({ isOpen: false });
    navigateWithQueryParams({ ...params, buildingName: building.buildingName, buildingId: String(building.id) });
  };

  // 적용기간(적용 시작일 ~ 적용 종료일) 기간검색
  const handleOnDateRangeChange = (range: [Date | null, Date | null]) => {
    setDateRange(range);
    // 둘 다 비었으면 검색 조건 제거
    if (range[0] === null && range[1] === null) {
      navigateWithQueryParams({ ...params, startDate: undefined, endDate: undefined, page: 0 });
      return;
    }
    // 시작/종료일이 모두 선택되면 검색 실행
    if (range[0] && range[1]) {
      const start = moment(range[0]).format(YmdFormat.YYYY_MM_DD) + "T00:00:00.000+09:00";
      const end = moment(range[1]).format(YmdFormat.YYYY_MM_DD) + "T23:59:59.999+09:00";
      navigateWithQueryParams({ ...params, startDate: start, endDate: end, page: 0 });
    }
  };

  useEffect(() => {
    setParams({ ...queryParams }); // input 초기화
    const fetchApi = async (priceParams: QueryParams) => {
      // 목록 조회
      let { providerName, buildingName, buildingCommonFacilityId, ...payload } = priceParams;
      // 검색 타입에 따라 불필요한 검색 파라미터 정리
      // - 공용공간 요금 ID 검색: priceRuleIdList 사용
      // - 정책명 검색: searchType(RULE_NAME) + keywordList 사용
      if (payload.searchType === "PRICE_RULE_ID") {
        delete payload.searchType;
        delete payload.keywordList;
      } else {
        delete payload.priceRuleIdList;
      }
      const response = await getPriceList(payload);
      const priceData = response.data.data.content;

      // provider목록 조회해서 providerName 매핑
      const removeDuplicateProvider = new Set(priceData.map((price) => price.providerId));
      const removeDuplicateBuildingFacility = new Set(priceData.map((price) => price.buildingCommonFacilityId));

      const providerListString = [...removeDuplicateProvider].filter((item) => item !== null).join(",");
      const buildingFacilityListString = [...removeDuplicateBuildingFacility].filter((item) => item !== null).join(",");

      const providerData: ProviderModel[] = await getProvider({ providerId: providerListString })
        .then((providerResponse) => providerResponse.data.data.content)
        .catch(() => []);

      // commonFacility 목록 조회해서 buildingName, facilityName 매핑
      const facilityData: CommonFacilityModel[] = await getCommonFacilityList({
        searchType: "ID",
        keywordList: buildingFacilityListString,
        commonFacilityTypeList: "",
      })
        .then((buildingFacilityResponse) => buildingFacilityResponse.data.data.content)
        .catch(() => []);

      const forColumnPriceData = priceData.map((price) => {
        const findProviderName = providerData.find((provider) => provider.providerId === price.providerId)?.providerName;
        const findBuildingName = facilityData.find((facility) => facility.id === price.buildingCommonFacilityId)?.buildingName;
        const findFacilityName = facilityData.find((facility) => facility.id === price.buildingCommonFacilityId)?.facilityName;
        return { ...price, providerName: findProviderName || "", buildingName: findBuildingName || "", facilityName: findFacilityName || "" };
      });

      setPriceList(forColumnPriceData);
      setPageMeta(response.data.meta.pageMeta);
    };

    fetchApi(queryParams);
  }, [queryParams, getPriceList, getProvider, getCommonFacilityList]);

  return (
    <div className="">
      <div className="contents-container__table">
        <div className="contents-container__search-wrap">
          <div className="left-area">
            {/* section 태그가 지정검색, 조건검색, 영역이고 반드시 필요함 */}
            <section>
              <div className="left-area__index">
                <span>지정검색</span>
              </div>
              <div className="left-area__contents">
                <div className="minmax160">
                  <BaseInput
                    type="text"
                    value={params?.providerName || ""}
                    placeholder="프로바이더"
                    readonly={true}
                    onClick={(e: any) => {
                      e.preventDefault();
                      setIsProviderModalOpen({ isOpen: true });
                    }}
                    onClearClick={() => {
                      let { providerName, providerId, ...rest } = params;
                      navigateWithQueryParams({ ...rest });
                    }}
                  />
                  {isProviderModalOpen.isOpen && (
                    <ProviderSelectModal
                      onCanceled={() => setIsProviderModalOpen({ isOpen: false })}
                      onAdded={onAddedProviderSelectModal}
                      defaultChecked={params.providerName ? [{ providerId: String(params.providerId), providerName: params.providerName }] : []}
                    />
                  )}
                </div>
                <div className="minmax160">
                  <BaseInput
                    type="text"
                    value={params?.buildingName || ""}
                    placeholder="건물명"
                    readonly={true}
                    onClick={(e: any) => {
                      e.preventDefault();
                      setIsBuildingModalOpen({ isOpen: true });
                    }}
                    onClearClick={() => {
                      let { buildingName, buildingId, ...rest } = params;
                      navigateWithQueryParams({ ...rest });
                    }}
                  />
                  {isBuildingModalOpen.isOpen && (
                    <SearchBuildingPopup
                      buildingName={""}
                      isOpen={true}
                      defaultChecked={params.buildingName ? [{ buildingId: String(params.buildingId), buildingName: params.buildingName }] : []}
                      onClick={(building: any) => onAddedBuildingSelectModal(building)}
                      onClose={() => setIsBuildingModalOpen({ isOpen: false })}
                    />
                  )}
                </div>
              </div>
            </section>
            <section>
              <div className="left-area__index">
                <span>조건검색</span>
              </div>
              <div className="left-area__contents">
                <div>
                  <BaseInputWithSearch
                    type="text"
                    selectValue={params?.searchType ?? ""}
                    inputValue={(params?.searchType === "PRICE_RULE_ID" ? params?.priceRuleIdList : params?.keywordList) || ""}
                    stateOptions={searchTypes}
                    setStateValue={(searchType: string) => {
                      // 검색 타입 전환 시 반대편 검색어 초기화
                      setParams({ ...params, searchType, keywordList: "", priceRuleIdList: "" });
                    }}
                    onClearClick={() => navigateWithQueryParams({ ...params, keywordList: "", priceRuleIdList: "" })}
                    onChange={(value: string) =>
                      params?.searchType === "PRICE_RULE_ID"
                        ? setParams({ ...params, priceRuleIdList: value })
                        : setParams({ ...params, keywordList: value })
                    }
                    onKeyUp={() => navigateWithQueryParams({ page: 0 }, "search")}
                    onSearchClick={() => navigateWithQueryParams({ page: 0 }, "search")}
                  />
                </div>
                <BaseSelect
                  placeholder="요금 유형"
                  className="minmax160"
                  stateOptions={ruleTypes}
                  value={params?.priceRuleType}
                  setStateValue={(value: string) => {
                    navigateWithQueryParams({ ...params, priceRuleType: value });
                  }}
                />

                <div className="minmax240">
                  <RangeDatepicker
                    dateRange={dateRange}
                    placeholder="적용기간 검색"
                    onChange={handleOnDateRangeChange}
                    onClearClick={() => {
                      setDateRange([null, null]);
                      navigateWithQueryParams({ ...params, startDate: undefined, endDate: undefined, page: 0 });
                    }}
                  />
                </div>

                <SearchResetButton />
              </div>
            </section>
          </div>
          <div className="right-area">
            <div className="flex-center">
              {/* <BaseButton
                title="현재 적용된 기준요금"
                className="mr10 color-white"
                onClick={() => {
                  setIsAppliedBasePriceModalOpen({ isOpen: true });
                }}
              /> */}
              {isAuthority("w") && (
                <BaseButton
                  title="+ 요금 등록"
                  onClick={() => {
                    navigate(PagePath.price.form);
                  }}
                />
              )}
            </div>
          </div>
          {isAppliedBasePriceModalOpen.isOpen && (
            <AppliedBasePriceModal
              isOpen={true}
              onClose={() => {
                setIsAppliedBasePriceModalOpen({ isOpen: false });
              }}
            />
          )}
        </div>

        {priceList && (
          <BaseTable
            data={priceList}
            columns={priceListColumn}
            pageIndex={pageMeta?.pageRequest.page || 0}
            totalPages={pageMeta?.totalPages || 0}
            goPage={(page: number) => {
              navigateWithQueryParams({ page }, "pagination");
            }}
            totalElements={Number(pageMeta?.totalElements) || 0}
            orders={queryParams?.sort?.orders}
            disabledSortHeaders={["providerName", "buildingName", "facilityName", "priceRuleType2"]}
            sizeOption={(number: number) => {
              setParams({ ...params, size: number });
              navigateWithQueryParams({ ...params, page: 0, size: number }, "search");
            }}
            currentSize={Number(queryParams.size) || 20}
            setOrders={(orders?: Array<Order>) => {
              if (orders) {
                navigateWithQueryParams({ ...params, sort: { orders } });
              }
            }}
          />
        )}
      </div>
    </div>
  );
};
export default PriceList;
